/**
 * GameStartingOverlay — a brief full-screen "preparing the game" beat shown to
 * players sitting in the lobby the moment it flips LOBBY → ACTIVE (auto-start on
 * the final join / bot, or a manual start).
 *
 * Why it exists: the transition is near-instant locally, which reads as an
 * abrupt snap into the board and gives no time to mask the client's state
 * hydration. The server has already run placement + randomized turn order by
 * the time we observe ACTIVE, so this is purely a client-side reveal delay with
 * a minimum floor (see START_PREPARE_MS in the game screen). The board mounts
 * and hydrates UNDERNEATH this overlay; when the floor elapses and state is
 * ready, the overlay drops away to reveal a settled game.
 *
 * Rendered as an absolute-fill layer (not a Modal) so it reliably covers the
 * map + HUD on both native and web, and blocks interaction while preparing.
 */

import React from 'react'
import { View, Text, ActivityIndicator } from 'react-native'

export function GameStartingOverlay({ gameName }: { gameName?: string | null }) {
  const name = gameName?.trim()
  return (
    <View
      // Blocks touches on the board while it settles underneath.
      accessibilityLabel={name ? `${name} is about to begin` : 'The game is about to begin'}
      accessibilityRole="alert"
      className="bg-canvas items-center justify-center gap-4 px-8"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60 }}
    >
      <ActivityIndicator size="large" color="#B91C1C" />
      <Text className="text-ink font-stencil text-lg uppercase tracking-widest text-center">
        Preparing the game
      </Text>
      <Text className="text-ink-soft font-typed text-sm text-center">
        {name ? `“${name}” is about to begin…` : 'The game is about to begin…'}
      </Text>
    </View>
  )
}
